/**
 * skilltree-layout.ts — 产业链地图布局算法（v4）
 *
 * v4 核心变化：
 *   - Tier 分级：节点大小代表战略重要性
 *   - Tier 1（核心瓶颈）= 大卡片 260×76
 *   - Tier 2（关键组件）= 中卡片 220×56
 *   - Tier 3（支撑组件）= 小卡片 180×42
 *   - 布局根据 Tier 调整垂直间距
 *   - 上游（矿物）在左 → 下游（系统）在右
 */

import type { ChainNode, ChainEdge } from '../types';

export interface LaidOutNode {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  cluster: string;
  layer: number;
  displayCol: number;
  tier: 1 | 2 | 3;
}

/** Tier 卡片尺寸 */
export const TIER_SIZES = {
  1: { width: 260, height: 76 },
  2: { width: 220, height: 56 },
  3: { width: 180, height: 42 },
} as const;

/** 横向列宽（像素）— 容纳最大卡片 + 间距 */
export const COLUMN_WIDTH = 300;

/** 同 type 子簇内节点垂直间距（像素） */
export const NODE_GAP_Y = 8;

/** 同层内 type 子簇之间留白（像素） */
export const TYPE_GAP = 16;

/** 层级表头 Y 坐标 */
export const LAYER_HEADER_Y = -36;

/** Barycenter 排序迭代次数 */
const BARYCENTER_ITERATIONS = 4;

/**
 * 判断是否为核心节点（overview 模式过滤用）
 */
export function isCoreNode(node: ChainNode): boolean {
  if (node.layer <= 1) return true;
  if (node.layer >= 3 && node.layer <= 4) return true;
  if (node.layer >= 7) return true;
  if (node.layer === 2) return node.bottleneck_level === 'high';
  return node.bottleneck_level === 'high';
}

/**
 * 计算 Tier 分级
 * @param node 节点
 * @param avgIps 平均 IPS
 * @param isToll 是否收费站
 * @param hasInvestable 是否有可投资标的
 */
export function computeTier(
  node: ChainNode,
  avgIps: number,
  isToll: boolean,
  hasInvestable: boolean
): 1 | 2 | 3 {
  // Tier 1: 核心瓶颈
  if (node.bottleneck_level === 'high' && (isToll || avgIps >= 70)) return 1;
  // Tier 2: 关键组件
  if (node.bottleneck_level === 'high') return 2;
  if (node.bottleneck_level === 'medium' && hasInvestable) return 2;
  // Tier 3: 支撑组件
  return 3;
}

/**
 * 主布局函数
 */
export function computeSkillTreeLayout(
  nodes: ChainNode[],
  edges: ChainEdge[],
  tierMap?: Map<string, 1 | 2 | 3>
): Map<string, LaidOutNode> {
  const nodeMap = new Map(nodes.map(n => [n.id, n]));

  // 1) 按 layer 分组
  const byLayer = new Map<number, ChainNode[]>();
  for (const n of nodes) {
    if (!byLayer.has(n.layer)) byLayer.set(n.layer, []);
    byLayer.get(n.layer)!.push(n);
  }
  const maxLayer = Math.max(0, ...byLayer.keys());

  // 2) 每层按 type 子簇分组
  const layerClusters: Array<Array<{ type: string; ids: string[] }>> = [];
  for (let layer = 0; layer <= maxLayer; layer++) {
    const layerNodes = byLayer.get(layer) ?? [];
    const byType = new Map<string, string[]>();
    for (const n of layerNodes) {
      if (!byType.has(n.type)) byType.set(n.type, []);
      byType.get(n.type)!.push(n.id);
    }
    const clusters = Array.from(byType.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([type, ids]) => ({ type, ids: ids.slice().sort() }));
    layerClusters.push(clusters);
  }

  // 3) 邻接表
  const upNeighbors = new Map<string, string[]>();
  const downNeighbors = new Map<string, string[]>();
  for (const e of edges) {
    if (!upNeighbors.has(e.target)) upNeighbors.set(e.target, []);
    upNeighbors.get(e.target)!.push(e.source);
    if (!downNeighbors.has(e.source)) downNeighbors.set(e.source, []);
    downNeighbors.get(e.source)!.push(e.target);
  }

  // 4) barycenter 排序
  for (let iter = 0; iter < BARYCENTER_ITERATIONS; iter++) {
    const lookUp = iter % 2 === 0;
    for (let layer = 0; layer <= maxLayer; layer++) {
      const clusters = layerClusters[layer];
      for (const cluster of clusters) {
        cluster.ids = sortByBarycenter(cluster.ids, lookUp ? upNeighbors : downNeighbors, layerClusters, lookUp, layer);
      }
      clusters.sort((a, b) => clusterCenter(a, layerClusters) - clusterCenter(b, layerClusters));
    }
  }

  // 5) 计算 Y 坐标 — 根据 Tier 调整步距
  const yById = new Map<string, number>();
  for (let layer = 0; layer <= maxLayer; layer++) {
    const clusters = layerClusters[layer];
    let cursorY = 0;
    for (const cluster of clusters) {
      for (let i = 0; i < cluster.ids.length; i++) {
        const id = cluster.ids[i];
        const tier = tierMap?.get(id) ?? 3;
        const height = TIER_SIZES[tier].height;
        yById.set(id, cursorY);
        cursorY += height + NODE_GAP_Y;
      }
      cursorY += TYPE_GAP;
    }
  }

  // 6) 装配 — 反转层级：displayCol = maxLayer - layer
  const result = new Map<string, LaidOutNode>();
  for (let layer = 0; layer <= maxLayer; layer++) {
    for (const cluster of layerClusters[layer]) {
      for (const id of cluster.ids) {
        const node = nodeMap.get(id);
        if (!node) continue;
        const displayCol = maxLayer - layer;
        const tier = tierMap?.get(id) ?? 3;
        const size = TIER_SIZES[tier];
        result.set(id, {
          id,
          x: displayCol * COLUMN_WIDTH,
          y: yById.get(id) ?? 0,
          width: size.width,
          height: size.height,
          cluster: cluster.type,
          layer,
          displayCol,
          tier,
        });
      }
    }
  }

  return result;
}

// ---------- helpers ----------

function sortByBarycenter(
  ids: string[],
  neighbors: Map<string, string[]>,
  layerClusters: Array<Array<{ type: string; ids: string[] }>>,
  lookUp: boolean,
  layer: number
): string[] {
  const currentY = new Map<string, number>();
  for (let l = 0; l < layerClusters.length; l++) {
    let cursorY = 0;
    for (const cluster of layerClusters[l]) {
      cluster.ids.forEach((id) => {
        currentY.set(id, cursorY);
        cursorY += 60; // 近似值用于排序
      });
      cursorY += TYPE_GAP;
    }
  }

  const neighborLayer = lookUp ? layer - 1 : layer + 1;
  const neighborLayerClusters = layerClusters[neighborLayer];
  if (!neighborLayerClusters) return ids;

  const neighborY = new Map<string, number>();
  let cursorY = 0;
  for (const cluster of neighborLayerClusters) {
    cluster.ids.forEach((id) => {
      neighborY.set(id, cursorY);
      cursorY += 60;
    });
    cursorY += TYPE_GAP;
  }

  return ids.slice().sort((a, b) => {
    const na = neighbors.get(a) ?? [];
    const nb = neighbors.get(b) ?? [];
    if (!na.length && !nb.length) return a.localeCompare(b);
    if (!na.length) return 1;
    if (!nb.length) return -1;
    const ya = median(na.map(id => neighborY.get(id) ?? 0));
    const yb = median(nb.map(id => neighborY.get(id) ?? 0));
    return ya - yb;
  });
}

function median(arr: number[]): number {
  if (!arr.length) return 0;
  const sorted = arr.slice().sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

function clusterCenter(
  cluster: { type: string; ids: string[] },
  layerClusters: Array<Array<{ type: string; ids: string[] }>>,
): number {
  if (!cluster.ids.length) return 0;
  const yById = new Map<string, number>();
  for (const layer of layerClusters) {
    let cursorY = 0;
    for (const c of layer) {
      c.ids.forEach((id) => {
        yById.set(id, cursorY);
        cursorY += 60;
      });
      cursorY += TYPE_GAP;
    }
  }
  const ys = cluster.ids.map(id => yById.get(id) ?? 0);
  return ys.reduce((a, b) => a + b, 0) / ys.length;
}

/**
 * 主干识别：跨层边 + 至少一端 high/medium bottleneck + chains 交集 ≥ 1
 */
export function identifyMainTrunk(
  nodes: ChainNode[],
  edges: ChainEdge[]
): Set<string> {
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  const trunk = new Set<string>();

  for (const e of edges) {
    const src = nodeMap.get(e.source);
    const tgt = nodeMap.get(e.target);
    if (!src || !tgt) continue;
    if (src.layer === tgt.layer) continue;
    const srcImportant = src.bottleneck_level === 'high' || src.bottleneck_level === 'medium';
    const tgtImportant = tgt.bottleneck_level === 'high' || tgt.bottleneck_level === 'medium';
    if (!srcImportant && !tgtImportant) continue;
    const intersection = (src.chains ?? []).filter(c => (tgt.chains ?? []).includes(c));
    if (intersection.length >= 1) trunk.add(e.id);
  }

  return trunk;
}

/**
 * 纵向路径布局算法 — Metroidvania 展开模式
 *
 * 从 focusNode 向上下游追溯到最小节点，生成单列纵向布局。
 * 用于移动端点击节点后的路径展开视图。
 */
export interface PathLayoutNode {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  layer: number;  // 在路径中的层级（0=最上游）
}

export function computePathLayout(
  nodes: ChainNode[],
  edges: ChainEdge[],
  focusNodeId: string,
  nodeSize?: { width: number; height: number }
): { nodes: Map<string, PathLayoutNode>; pathEdges: ChainEdge[] } {
  const size = nodeSize ?? { width: 280, height: 60 };
  const NODE_GAP_Y = 20;

  const nodeMap = new Map(nodes.map(n => [n.id, n]));

  // 构建上下游邻接表
  const upNeighbors = new Map<string, string[]>();  // target -> sources
  const downNeighbors = new Map<string, string[]>();  // source -> targets
  for (const e of edges) {
    if (!upNeighbors.has(e.target)) upNeighbors.set(e.target, []);
    upNeighbors.get(e.target)!.push(e.source);
    if (!downNeighbors.has(e.source)) downNeighbors.set(e.source, []);
    downNeighbors.get(e.source)!.push(e.target);
  }

  // BFS 从 focusNode 向上追溯到最上游
  const upstreamPath: string[] = [];
  const visitedUp = new Set<string>();
  let currentUp: string | null = focusNodeId;
  while (currentUp && !visitedUp.has(currentUp)) {
    visitedUp.add(currentUp);
    upstreamPath.unshift(currentUp);
    const parents: string[] = upNeighbors.get(currentUp) ?? [];
    // 沿着 bottleneck_level 最关键的父节点走
    let nextUp: string | null = null;
    if (parents.length > 0) {
      const sorted = parents.slice().sort((a: string, b: string) => {
        const na = nodeMap.get(a);
        const nb = nodeMap.get(b);
        const priority = (n: ChainNode | undefined): number => {
          if (!n) return 0;
          if (n.bottleneck_level === 'high') return 3;
          if (n.bottleneck_level === 'medium') return 2;
          return 1;
        };
        return priority(nb) - priority(na);
      });
      nextUp = sorted[0] ?? null;
    }
    currentUp = nextUp && !visitedUp.has(nextUp) ? nextUp : null;
  }

  // BFS 从 focusNode 向下追溯到最下游
  const downstreamPath: string[] = [];
  const visitedDown = new Set<string>();
  let currentDown: string | null = focusNodeId;
  while (currentDown && !visitedDown.has(currentDown)) {
    visitedDown.add(currentDown);
    if (currentDown !== focusNodeId) downstreamPath.push(currentDown);
    const children: string[] = downNeighbors.get(currentDown) ?? [];
    let nextDown: string | null = null;
    if (children.length > 0) {
      const sorted = children.slice().sort((a: string, b: string) => {
        const na = nodeMap.get(a);
        const nb = nodeMap.get(b);
        const priority = (n: ChainNode | undefined): number => {
          if (!n) return 0;
          if (n.bottleneck_level === 'high') return 3;
          if (n.bottleneck_level === 'medium') return 2;
          return 1;
        };
        return priority(nb) - priority(na);
      });
      nextDown = sorted[0] ?? null;
    }
    currentDown = nextDown && !visitedDown.has(nextDown) ? nextDown : null;
  }

  // 合并完整路径
  const fullPath = [...upstreamPath, ...downstreamPath.filter(id => id !== focusNodeId)];

  // 计算节点位置
  const result = new Map<string, PathLayoutNode>();
  for (let i = 0; i < fullPath.length; i++) {
    const nodeId = fullPath[i];
    result.set(nodeId, {
      id: nodeId,
      x: 0,
      y: i * (size.height + NODE_GAP_Y),
      width: size.width,
      height: size.height,
      layer: i,
    });
  }

  // 收集路径边
  const pathNodeSet = new Set(fullPath);
  const pathEdges = edges.filter(e =>
    pathNodeSet.has(e.source) && pathNodeSet.has(e.target)
  );

  return { nodes: result, pathEdges };
}

/**
 * 获取路径中的边关系标签（用于显示）
 */
export function getEdgeRelationLabel(relation: string): string {
  const labels: Record<string, string> = {
    'input_to': '→ 原料',
    'part_of': '→ 组成',
    'made_by': '→ 制造',
    'substitute_for': '→ 替代',
  };
  return labels[relation] ?? `→ ${relation}`;
}
